import { useEffect, useState, type FormEvent } from "react";
import { useI18n } from "../i18n";
import { acknowledgePolicy, loadMyPolicies, loadPolicyStatus, publishPolicy, type MyPolicy, type PolicyStatus } from "../data";
import type { Workspace } from "../types";

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").replace(/^(\d)/, "p_$1").slice(0, 40) || "policy";

/** Everyone reads and acknowledges current policies; HR publishes versions and tracks who has acknowledged. */
export function Policies({ ws }: { ws: Workspace }) {
  const { t, pick } = useI18n();
  const cid = ws.membership.company_id;
  const canManage = ws.permissions.has("policies.manage");
  const [mine, setMine] = useState<MyPolicy[] | null>(null);
  const [status, setStatus] = useState<PolicyStatus[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState<MyPolicy | "new" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    try {
      setMine(await loadMyPolicies(cid));
      if (canManage) setStatus(await loadPolicyStatus(cid));
    } catch (e) { setError(errText(e, t("errorGeneric"))); }
  };
  useEffect(() => { void refresh(); }, [cid]);

  async function ack(id: string) {
    setError(null);
    try { await acknowledgePolicy(id); await refresh(); } catch (e) { setError(errText(e, t("errorGeneric"))); }
  }

  const todo = (mine ?? []).filter((p) => p.requires_ack && !p.acknowledged_at);
  const stat = (id: string) => status.find((s) => s.policy_id === id);

  return (
    <div className="page">
      {error && <p className="alert danger" role="alert">{error}</p>}
      {ws.membership.employment_id && todo.length > 0 && (
        <p className="alert info">{t("toAcknowledge")}: {todo.length}</p>
      )}
      {canManage && (
        <div className="toolbar"><button type="button" className="btn primary" onClick={() => setEditing("new")}>{t("publishPolicy")}</button></div>
      )}
      {editing && <PolicyForm ws={ws} base={editing === "new" ? null : editing} onDone={async () => { setEditing(null); await refresh(); }} onCancel={() => setEditing(null)} />}
      <section className="card">
        {mine === null ? <p className="muted">{t("loading")}</p> : mine.length === 0 ? <p className="muted">{t("noPolicies")}</p> : (
          <ul className="plain-list">
            {mine.map((p) => {
              const s = stat(p.policy_id);
              const body = pick(p.body_en, p.body_ar);
              const isAr = body === p.body_ar && !!p.body_ar;
              return (
                <li key={p.policy_id} className="policy">
                  <div className="row spread">
                    <button type="button" className="link strong" aria-expanded={open === p.policy_id} onClick={() => setOpen(open === p.policy_id ? null : p.policy_id)}>
                      {pick(p.title_en, p.title_ar)}
                    </button>
                    <span className="row">
                      <span className="muted small-note">{t("version")} {p.version} · <span className="mono">{p.published_at.slice(0, 10)}</span></span>
                      {p.acknowledged_at ? <span className="badge success">{t("acknowledged")}</span>
                        : p.requires_ack && ws.membership.employment_id ? <span className="badge warning">{t("toAcknowledge")}</span> : null}
                    </span>
                  </div>
                  {s && s.requires_ack && (
                    <div className="row" aria-label={t("progress")}>
                      <div className="meter" style={{ flex: 1 }}><span style={{ width: `${s.employees ? (100 * s.acknowledged) / s.employees : 0}%` }} /></div>
                      <span className="mono small-note">{s.acknowledged}/{s.employees}</span>
                    </div>
                  )}
                  {open === p.policy_id && (
                    <>
                      <div className="policy-body" dir={isAr ? "rtl" : "ltr"}>{body}</div>
                      <div className="row">
                        {p.requires_ack && !p.acknowledged_at && ws.membership.employment_id && (
                          <button type="button" className="btn primary small" onClick={() => ack(p.policy_id)}>{t("acknowledge")}</button>
                        )}
                        {canManage && <button type="button" className="btn secondary small" onClick={() => setEditing(p)}>{t("newVersion")}</button>}
                      </div>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function PolicyForm({ ws, base, onDone, onCancel }: { ws: Workspace; base: MyPolicy | null; onDone: () => Promise<void>; onCancel: () => void }) {
  const { t } = useI18n();
  const [f, setF] = useState({
    title_en: base?.title_en ?? "", title_ar: base?.title_ar ?? "", body_en: base?.body_en ?? "", body_ar: base?.body_ar ?? "",
    requires_ack: base?.requires_ack ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await publishPolicy(ws.membership.company_id, base?.key ?? slug(f.title_en), f.title_en, f.body_en, f.title_ar, f.body_ar, f.requires_ack);
      await onDone();
    } catch (err) { setError(errText(err, t("errorGeneric"))); }
    finally { setBusy(false); }
  }
  return (
    <form className="card policy-form" onSubmit={save}>
      <h2>{base ? t("newVersion") : t("publishPolicy")}</h2>
      <div className="field-row">
        <label className="field"><span>{t("policyTitle")}</span><input value={f.title_en} onChange={(e) => setF({ ...f, title_en: e.target.value })} required /></label>
        <label className="field"><span>{t("policyTitleAr")}</span><input dir="rtl" value={f.title_ar} onChange={(e) => setF({ ...f, title_ar: e.target.value })} /></label>
      </div>
      <label className="field"><span>{t("policyBody")}</span><textarea rows={10} value={f.body_en} onChange={(e) => setF({ ...f, body_en: e.target.value })} required /></label>
      <label className="field"><span>{t("policyBodyAr")}</span><textarea rows={10} dir="rtl" value={f.body_ar} onChange={(e) => setF({ ...f, body_ar: e.target.value })} /></label>
      <label className="check"><input type="checkbox" checked={f.requires_ack} onChange={(e) => setF({ ...f, requires_ack: e.target.checked })} /> {t("requiresAck")}</label>
      {error && <p className="alert danger" role="alert">{error}</p>}
      <div className="actions">
        <button type="button" className="btn secondary" onClick={onCancel}>{t("cancel")}</button>
        <button className="btn primary" disabled={busy}>{t("publish")}</button>
      </div>
    </form>
  );
}
